import { IFinOpsBaseline, IFinOpsBudget, IFinOpsBudgetAllocationCombined } from "../models/FinOpsBudgets";

import { AccountInfo } from "@azure/msal-browser";
import { LineChartSeries } from "../components/common/LineChart";
import moment from "moment";
import { toNumber } from "lodash";

const Categories = [
    'Compute:AIRS:Public Compute VMs:Cores',
    'Compute:AIRS:Cosmic:Cores',
    'Compute:AIRS:Legacy GPU:Cores',
    'Network:MCIO Network:LongHaul T0:Mbps',
    'Network:MCIO Network:LongHaul T2:Mbps',
    'Network:MCIO Network:Egress:Mbps',
    'Storage:AIRS:Transactions:10K',
    'Storage:AIRS:Storage - Other:GB',
    'Storage:AIRS:Bandwidth:GB',
    'Storage:AIRS:Managed SSD Disks:GB',
    'Storage:AIRS:Managed HDD Disks:GB',
    'Storage:AIRS:Other:',
    'Others:AIRS:Others:',
    'Monitoring:AIRS:SQL:',
    'Monitoring:AIRS:Redis:',
    'Data Operations:AIRS:ADF:',
    'Monitoring:AIRS:Cosmos DB:RU/s',
    'Monitoring:AIRS:Cosmos DB:GB',
    'Monitoring:AIRS:Cosmos DB:',
    'Monitoring:AIRS:Azure Front Door:',
    'Monitoring:AIRS:Azure Front Door:GB',
    'Monitoring:AIRS:Azure Front Door:10K',
    'Monitoring:AIRS:Azure Log Analytics:GB',
    'Monitoring:AIRS:Azure Log Analytics:',
    'Monitoring:AIRS:Synapse Analytics:',
    'Monitoring:AIRS:Synapse Analytics:vCore',
    'Monitoring:AIRS:Synapse Analytics:GB',
    'Monitoring:AIRS:Synapse Analytics:DWUs',
    'Network:AIRS:Azure Bandwidth:GB',
    'Monitoring:AIRS:Azure Monitor:',
    'Monitoring:AIRS:Azure Monitor:1 GB',
    'Monitoring:AIRS:Azure Monitor:10M',
    'Monitoring:AIRS:Azure Monitor:1GB/Day',
    'Monitoring:AIRS:Azure Monitor:1M',
    'Passive Monitoring:AIRS:GenevaMdm:WeightedTimeSeriesCount',
    'Passive Monitoring:AIRS:GenevaMdm:EventRate/Min',
    'Passive Monitoring:Non-AIRS:GenevaMdsIndirect:GB',
    'Passive Monitoring:AIRS:GenevaMds:GB',
    'Passive Monitoring:AIRS:Kusto:GB',
    'Passive Monitoring:AIRS:Kusto:CPU',
];

function blankBudget(
    fiscalYear: string,
    serviceId: string,
    category: string,
    type: string,
    unit: string): IFinOpsBudget {
    return {
        id: 0,
        fiscalYear: fiscalYear,
        serviceId: serviceId,
        category: category,
        type: type,
        lastYearCost: 0,
        lastYearQuantity: 0,
        unit: unit,
        increaseRate: 0,
        quantityBudget: 0,
        costBudget: 0
    };
}

function getCategory(category: string, type: string) {
    switch (category) {
        case 'Network':
            if (type === 'Azure Bandwidth') return 'AIRS';
            else return 'MCIO Network';
        case 'Passive Monitoring':
            if (type === 'GenevaMdsIndirect') return 'Non-AIRS';
            else return 'AIRS';
        default:
            return 'AIRS';
    }
}

export function suppleBudgets(
    fiscalYear: string,
    serviceId: string,
    budgets: IFinOpsBudget[]) {
    const set = new Set(Categories);
    budgets.forEach(budget => set.delete(`${budget.category}:${getCategory(budget.category, budget.type)}:${budget.type}:${budget.unit || ''}`));
    return [
        ...budgets.filter(budget => budget.category !== null),
        ...Array.from(set).map(item => {
            const [category, _, type, unit] = item.split(':');
            return blankBudget(fiscalYear, serviceId, category, type, unit);
        })
    ]
}

export function clearEmptyBudgets(budgets?: IFinOpsBudget[]) {
    if (!budgets) return budgets;
    return budgets.filter(budget => budget.lastYearCost !== 0 || budget.lastYearQuantity !== 0 || budget.costBudget !== 0 || budget.submitter);
}

export function getLastFiscalYear(fiscalYear: string) {
    if (fiscalYear.length < 4) return fiscalYear;
    return `FY${parseInt(fiscalYear.substring(2)) - 1}`;
}

export function getBudgetFiscalYear() {
    const now = moment();
    const month = now.month();
    return 'FY' + (parseInt(now.format('YY')) + 1 + (month > 6 ? 1 : 0));
}

export function getBudgetId(budget: IFinOpsBudget) {
    return `${budget.serviceId}-${budget.category}-${budget.type}-${budget.unit}`;
}

export function recalculateServiceBudget(serviceBudget: IFinOpsBudget) {
    const costBudget = serviceBudget.children?.reduce((sum, budget) => sum + budget.costBudget, 0) || serviceBudget.costBudget;
    const lastYearServiceCost = serviceBudget.children?.reduce((sum, budget) => sum + budget.lastYearCost, 0) || serviceBudget.lastYearCost;

    return {
        ...serviceBudget,
        costBudget,
        increaseRate: lastYearServiceCost !== 0 ? toNumber((costBudget / lastYearServiceCost * 100 - 100).toFixed(1)) : 0
    }
}

const DefaultBlendedRates: { [key: string]: number } = {
    "Monitoring:Cosmos DB:GB":0.118401124736337,
    "Monitoring:Cosmos DB:":0.0131378867625526,
    "Monitoring:Cosmos DB:RU/s":0.00297755476233779,
    "Monitoring:Azure Log Analytics:GB":0.0679907872981772,
    "Monitoring:Azure Log Analytics:":0.436729136856282,
    "Monitoring:Azure Monitor:":0.00089881709594122,
    "Monitoring:Azure Monitor:10M":0.0364902860630842,
    "Monitoring:Azure Monitor:1GB/Day":3010.21711347686,
    "Monitoring:Azure Monitor:1 GB":0.0569917563371282,
    "Monitoring:Azure Front Door:GB":0.0113577726910489,
    "Monitoring:Azure Front Door:":0.205707160523623,
    "Monitoring:Azure Front Door:10K":0.00315311413265204,
    "Monitoring:Synapse Analytics:GB":0.109922410374672,
    "Monitoring:Synapse Analytics:":0.0539877434824353,
    "Monitoring:Synapse Analytics:vCore":0.0387166784554428,
    "Monitoring:Synapse Analytics:DWUs":0.724478540768339,
    "Storage:Transactions:10K":0.000951771458682175,
    "Storage:Storage - Other:GB":0.00326985316666074,
    "Storage:Bandwidth:GB":0.00408862357888628,
    "Storage:Managed SSD Disks:GB":0.0661396633482012,
    "Storage:Managed HDD Disks:GB":0.00941965809295017,
    "Network:Azure Bandwidth:GB":0.00460920451366063,
    "Network:LongHaul T0:Mbps":0.867003946686895,
    "Network:Egress:Mbps":0.583210175995812,
    "Network:LongHaul T2:Mbps":0.476756391191679,
    "Network:Metro:Mbps":11.4554386323427,
    "Compute:Public Compute VMs:Cores":8.47868179963876,
    "Compute:Legacy GPU:Cores":16.7203830671522,
    "Compute:Cosmic:Cores":5.30048554699674,
    "Passive Monitoring:Kusto:GB":0.000507778117408832,
    "Passive Monitoring:Kusto:CPU":4.81646369944787E-05,
    "Passive Monitoring:GenevaMds:GB":0.000412587276213039,
    "Passive Monitoring:GenevaMdm:EventRate/Min":1.76095598552661E-06,
    "Passive Monitoring:GenevaMdm:WeightedTimeSeriesCount":1.63398923114378E-06,
    "Passive Monitoring:GenevaMdsIndirect:GB":0.00876665373727924,
}

export function getDefaultBlendedRate(budget: IFinOpsBudget) {
    return DefaultBlendedRates[`${budget.category}:${budget.type}:${budget.unit}`] || 0;
}

export function getFiscalYearRange(fiscalYear: string) {
  const yearNum = parseInt("20" + fiscalYear.slice(2), 10);
  const start = new Date(Date.UTC(yearNum - 1, 5, 1));
  const end = new Date(Date.UTC(yearNum, 4, 31, 23, 59, 59, 999));
  return { start, end };
}

export function getCurrentMonth() {
  const now = new Date(Date.now());
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();

  if (now.getUTCDate() >= 11) {
    return new Date(Date.UTC(year, month, 1));
  } else {
      if (month === 0) {
        return new Date(Date.UTC(year - 1, 11, 1));
    } else {
      return new Date(Date.UTC(year, month - 1, 1));
    }
  }
}

export function getPreviousMonth() {
  const currentMonth = getCurrentMonth();
  currentMonth.setMonth(currentMonth.getMonth() - 1);
  return currentMonth;
}

export function formatYYYYMM(date: Date) {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  return `${year}-${month}`;
}

export function getAllMonthsBetween(start: Date, end: Date): string[] {
  const months: string[] = [];
  let current = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));

  while (current <= end) {
    const year = current.getUTCFullYear();
    const month = (current.getUTCMonth() + 1).toString().padStart(2, "0");
    months.push(`${year}-${month}`);
    current = new Date(Date.UTC(current.getUTCFullYear(), current.getUTCMonth() + 1, 1));
  }
  return months;
}

export function buildChartSeries(
  data: IFinOpsBudgetAllocationCombined[],
  currentMonth: Date
): LineChartSeries[] {
  const groupedByMonth = new Map<string, { month: string; cost: number; budget: number }>();
  const months = Array.from(new Set(data.map(item => item.month)));
  months.forEach(d => d && groupedByMonth.set(d.slice(0, 7), { month: d.slice(0, 7), cost: 0, budget: 0 }));
  data.forEach((d) => {
    const month = d.month?.slice(0, 7);
    if (!month || !groupedByMonth.has(month)) return;
    const entry = groupedByMonth.get(month)!;
    entry.cost += d.cost ?? 0;
    entry.budget += d.budget ?? 0;
  });

  const chartData = Array.from(groupedByMonth.values()).sort(
    (a, b) => getUTCMonthStartDate(a.month).getTime() - getUTCMonthStartDate(b.month).getTime()
  );

  const actualSeries: LineChartSeries = {
    id: "actual-cost",
    name: "Actual Cost",
    type: "area",
    color: "#5470C6",
    dashStyle: "Solid",
    data: chartData
      .filter((d) => getUTCMonthStartDate(d.month) < currentMonth)
      .map((d) => [getUTCMonthStartTime(d.month), d.cost ?? 0]),
  };

  const forecastSeries: LineChartSeries = {
    id: "forecast-cost",
    name: "Forecast Cost",
    type: "area",
    color: "#5470C6",
    dashStyle: "Dash",
    data:  chartData
      .filter((d) => getUTCMonthStartDate(d.month) >= currentMonth)
      .map((d) => [getUTCMonthStartTime(d.month), d.cost ?? 0]),
  };

  const previousPoint = chartData
  .filter((d) => getUTCMonthStartDate(d.month).getUTCMonth() < currentMonth.getUTCMonth() && getUTCMonthStartDate(d.month).getUTCFullYear() === currentMonth.getUTCFullYear())
  .slice(-1)[0];

  const currentPoint = chartData.find((d) => getUTCMonthStartDate(d.month).getUTCMonth() === currentMonth.getUTCMonth() && getUTCMonthStartDate(d.month).getUTCFullYear() === currentMonth.getUTCFullYear());

  const connectorSeries: LineChartSeries = {
    id: "connector",
    name: "",
    type: "area",
    color: "#5470C6",
    dashStyle: "Solid",
    data: previousPoint && currentPoint
      ? [
          [getUTCMonthStartTime(previousPoint.month), previousPoint.cost ?? 0],
          [getUTCMonthStartTime(currentPoint.month), currentPoint.cost ?? 0],
        ]
      : [],
  };

  const budgetSeries: LineChartSeries = {
    id: "budget",
    name: "Budget",
    type: "area",
    color: "#EE6666",
    dashStyle: "Solid",
    data: chartData.map((d) => [getUTCMonthStartTime(d.month), d.budget ?? 0]),
  };

  return [actualSeries, forecastSeries, connectorSeries, budgetSeries];
}

export function roundAndBalanceBudgets(
  originalBudgets: Record<string, number>,
  targetTotal: number
): Record<string, number> {
  type Item = {
    key: string;
    original: number;
    rounded: number;
  };

  const keys = Object.keys(originalBudgets);

  const rounded: Item[] = keys.map((key) => ({
    key,
    original: originalBudgets[key],
    rounded: Math.round(originalBudgets[key]),
  }));

  const total = rounded.reduce((sum, item) => sum + item.rounded, 0);
  const diff = targetTotal - total;

  if (diff !== 0) {
    const sortBy = diff > 0
      ? (a: Item, b: Item) => (b.original - b.rounded) - (a.original - a.rounded)
      : (a: Item, b: Item) => (a.original - a.rounded) - (b.original - b.rounded);

    rounded.sort(sortBy);

    for (let i = 0; i < Math.abs(diff); i++) {
      rounded[i].rounded += diff > 0 ? 1 : -1;
    }
  }

  const result: Record<string, number> = {};
  for (const { key, rounded: roundedValue } of rounded) {
    result[key] = roundedValue;
  }
  return result;
}

export function normalizeData(data: IFinOpsBudgetAllocationCombined[]) {
  return (data || []).map(d => ({
    ...d,
    cost: Math.round(d.cost ?? 0),
    budget: Math.round(d.budget ?? 0),
  }));
}

export function generatePivotedData(
  normalizedData: IFinOpsBudgetAllocationCombined[],
  fiscalStart: Date,
  fiscalEnd: Date,
  monthlyBudgets: Record<string, number>
) {
  const groupedByMonth: Record<string, IFinOpsBudgetAllocationCombined[]> = {};
  (normalizedData || []).forEach(item => {
    const month = item.month;
    if (!month) return;

    const date = new Date(new Date(month).getUTCFullYear(), new Date(month).getUTCMonth(), 1);
    if (date < fiscalStart || date > fiscalEnd) return;

    const monthLabel = date.toLocaleString('en-US', {
      month: 'long',
      year: 'numeric',
    });

    if (!groupedByMonth[monthLabel]) {
      groupedByMonth[monthLabel] = [];
    }
    groupedByMonth[monthLabel].push(item);
  });

  const result: any[] = [
    { metric: 'Cost' },
    { metric: 'Budget' }
  ];

  Object.entries(groupedByMonth).forEach(([monthLabel, items]) => {
    const totalCost = items.reduce((sum, item) => sum + (item.cost ?? 0), 0);

    result[0][monthLabel] = totalCost;
    result[1][monthLabel] = monthlyBudgets[monthLabel] ?? 0;
  });

  return result;
}

export interface PivotRow {
  metric: string;
  [month: string]: string | number | null;
}

export function generatePivotedYoYCostData(
  normalizedData: IFinOpsBudgetAllocationCombined[],
  fiscalStart: Date,
  prevFiscalStart: Date,
  currentFiscalYear: string,
  previousFiscalYear: string
) {
  const monthFormatter = new Intl.DateTimeFormat('en-US', { month: 'short' });
  const rowPrev: PivotRow = { metric: `Cost ${previousFiscalYear}` };
  const rowCurr: PivotRow = { metric: `Cost ${currentFiscalYear}` };
  const rowDelta: PivotRow = { metric: 'YoY ∆' };
  const rowPercent: PivotRow = { metric: 'YoY %' };

  for (let i = 0; i < 12; i++) {
    const prevMonth = new Date(prevFiscalStart);
    prevMonth.setMonth(prevMonth.getMonth() + i);
    const currMonth = new Date(fiscalStart);
    currMonth.setMonth(currMonth.getMonth() + i);

    const label = monthFormatter.format(prevMonth);
    const prevKey = prevMonth.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    const currKey = currMonth.toLocaleString('en-US', { month: 'long', year: 'numeric' });

    const prevCost = normalizedData
      .filter(item => item.month && new Date(item.month).toLocaleString('en-US', { month: 'long', year: 'numeric' }) === prevKey)
      .reduce((sum, item) => sum + (item.cost ?? 0), 0);

    const currCost = normalizedData
      .filter(item => item.month && new Date(item.month).toLocaleString('en-US', { month: 'long', year: 'numeric' }) === currKey)
      .reduce((sum, item) => sum + (item.cost ?? 0), 0);

    rowPrev[label] = prevCost;
    rowCurr[label] = currCost;
    rowDelta[label] = currCost - prevCost;
    rowPercent[label] = prevCost !== 0 ? (currCost - prevCost) / prevCost : null;
  }

  return [rowPrev, rowCurr, rowDelta, rowPercent];
}

export function isAIService(item: IFinOpsBudgetAllocationCombined): boolean {
  return !!item.serviceId && item.isAIService === 1;
}

export function isNonAIService(item: IFinOpsBudgetAllocationCombined): boolean {
  return !isAIService(item);
}

export function classifyCategory(item: IFinOpsBudgetAllocationCombined): string {
  const cat = item.category?.toLowerCase() ?? "";
  const type = item.type?.toLowerCase() ?? "";

  if (cat === "compute" && type === "cosmic") return "Cosmic";
  if (cat === "passive monitoring") return "Passive Monitoring";
  if (cat === "network" && ["longhaul t2", "longhaul t0", "egress"].includes(type)) {
    return "MCIO Network";
  }
  return "Azure";
}

export function getUTCMonthStartDate(input: string | Date): Date {
  let year: number;
  let month: number;

  if (typeof input === "string") {
    const parts = input.split("-");
    year = Number(parts[0]);
    month = Number(parts[1]) - 1;
  } else if (input instanceof Date) {
    year = input.getUTCFullYear();
    month = input.getUTCMonth();
  } else {
    throw new Error("Invalid input type for getUTCMonthStart");
  }

  return new Date(Date.UTC(year, month, 1));
}

export function getUTCMonthStartTime(input: string | Date): number {
  return new Date(new Date(input).getUTCFullYear(), new Date(input).getUTCMonth(), 1).getTime();
}

export function getUTCMonthStartString(input: string | Date): string {
  let year: number;
  let month: number;

  if (typeof input === "string") {
    const parts = input.split("-");
    year = Number(parts[0]);
    month = Number(parts[1]) - 1;
  } else if (input instanceof Date) {
    year = input.getUTCFullYear();
    month = input.getUTCMonth();
  } else {
    throw new Error("Invalid input type for getUTCMonthStartString");
  }

  return new Date(Date.UTC(year, month, 1)).toISOString();
}

export function getUTCMonthStartTimeByMonthName(input: string): Date {
  const date = new Date(`${input} UTC`);
  if (isNaN(date.getTime())) {
    throw new Error(`Invalid date format: ${input}`);
  }
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}